import * as React from 'react';
import { connect } from 'react-redux';

import { ButtonCheckbox } from '../../../../common/components/button_checkbox';
import { IAppState, Dispatch } from '../../../../common/state';
import { EFacility, IJsonQuery } from '../../../../json_query';
import { FDealType, FOfferType, isOfferTypeIncludes, isParamAvailable } from '../../../../utils/category';
import { keys } from '../../../../utils/keys';
import { changeFacility, changeFurniture } from '../../../state/advanced/facilities';
import { InlineParam, Row } from '../markup';

interface IFacility {
  name: string;
  type: EFacility;
}

const facilitiesProps: IFacility[] = [
  { name: 'Холодильник', type: EFacility.Fridge },
  { name: 'Стиральная машина', type: EFacility.Washer },
  { name: 'Кухонная мебель', type: EFacility.KitchenFurniture },
  { name: 'Интернет', type: EFacility.Internet },
  { name: 'Телевизор', type: EFacility.TV },
  { name: 'Кондиционер', type: EFacility.Conditioner },
  { name: 'Посудомоечная машина', type: EFacility.Dishwasher },
  { name: 'Телефон', type: EFacility.Phone },
  { name: 'Можно с детьми', type: EFacility.KidsAllowed },
  { name: 'Можно с животными', type: EFacility.PetsAllowed },
  { name: 'Ванна', type: EFacility.Bath },
  { name: 'Душевая кабина', type: EFacility.Shower },
];

const houseFacilitiesProps: IFacility[] = [
  { name: 'Баня', type: EFacility.Sauna },
  { name: 'Гараж', type: EFacility.Garage },
  { name: 'Бассейн', type: EFacility.Pool },
];

const saleHouseFacilitiesProps: IFacility[] = [
  { name: 'Телефон', type: EFacility.Phone },
  { name: 'Охрана', type: EFacility.Security },
];

interface IFurniture {
  name: string;
  value: boolean;
}

const furnitureProps: IFurniture[] = [
  { name: 'Мебель', value: true },
  { name: 'Без мебели', value: false },
];

const getFacilities = (offerType: FOfferType, dealType: FDealType): IFacility[] => {
  const isAvailable = isParamAvailable(dealType, offerType);

  if (isAvailable(FDealType.Rent, FOfferType.Urban)) {
    return facilitiesProps;
  } else if (isAvailable(FDealType.Rent, FOfferType.Suburban & ~FOfferType.Land)) {
    return facilitiesProps.concat(houseFacilitiesProps);
  } else if (isAvailable(FDealType.Sale, FOfferType.Suburban & ~FOfferType.Land)) {
    return houseFacilitiesProps.concat(saleHouseFacilitiesProps);
  } else {
    return houseFacilitiesProps;
  }
};

export interface IFacilitiesProps {
  offerType: FOfferType;
  dealType: FDealType;

  hasFurniture: boolean | undefined;
  onFurnitureChanged(value: boolean | undefined): void;

  facilities: EFacility[];
  onFacilitiesChanged(param: EFacility, value: boolean): void;
}

export const facilities: React.StatelessComponent<IFacilitiesProps> = props => {
  const { offerType, dealType } = props;

  const allowedFacilities = getFacilities(offerType, dealType);
  const title = 'Что нужно в ' + (isOfferTypeIncludes(offerType, FOfferType.Urban) ? 'квартире' : 'доме');

  const furniture =
    isOfferTypeIncludes(offerType, FOfferType.Urban) ||
    isParamAvailable(dealType, offerType)(FDealType.Rent, FOfferType.Suburban & ~FOfferType.Land)
      ? furnitureProps
      : null;

  return (
    <Row title={title}>
      {furniture &&
        furniture.map(furnitureItem => (
          <InlineParam key={furnitureItem.name}>
            <ButtonCheckbox
              onValueChange={value => props.onFurnitureChanged(value ? furnitureItem.value : undefined)}
              value={props.hasFurniture === furnitureItem.value}
            >
              {furnitureItem.name}
            </ButtonCheckbox>
          </InlineParam>
        ))}

      {allowedFacilities.map(facility => (
        <InlineParam key={facility.type}>
          <ButtonCheckbox
            onValueChange={value => props.onFacilitiesChanged(facility.type, value)}
            value={props.facilities.indexOf(facility.type) !== -1}
          >
            {facility.name}
          </ButtonCheckbox>
        </InlineParam>
      ))}
    </Row>
  );
};

function facilitiesFromJsonQuery(jsonQuery: IJsonQuery): EFacility[] {
  return keys(EFacility).reduce((previousValue, currentValue) => {
    const facility = EFacility[currentValue];

    if (jsonQuery[facility] !== undefined) {
      previousValue.push(facility);
    }

    return previousValue;
  }, [] as EFacility[]);
}

// tslint:disable-next-line:no-default-export
export default connect<
  {
    hasFurniture: boolean | undefined;
    facilities: EFacility[];
  },
  {
    onFacilitiesChanged(param: EFacility, value: boolean): void;
    onFurnitureChanged(value: boolean | undefined): void;
  },
  {
    offerType: FOfferType;
    dealType: FDealType;
  }
>(
  (state: IAppState) => ({
    hasFurniture: state.filters.jsonQuery.has_furniture && state.filters.jsonQuery.has_furniture.value,
    facilities: facilitiesFromJsonQuery(state.filters.jsonQuery),
  }),
  (dispatch: Dispatch) => ({
    onFacilitiesChanged: (param: EFacility, value: boolean) => {
      dispatch(changeFacility(param, value));
    },
    onFurnitureChanged: (value: boolean | undefined) => {
      dispatch(changeFurniture(value));
    },
  }),
)(facilities);
